"use client";

import { FetchMultiselect, type MultiSelectOption } from "@/fetch-ui/FetchMultiselect";

import { useEstimator } from "../../_context/EstimatorContext";
import { useAirlineSearch } from "../../_hooks/useAirlineSearch";
import { AIRLINE_MODE_OPTIONS, MAX_AIRLINE_PICKS } from "../../_logic/constants";
import type { AirlineMode } from "../../_logic/types";

/**
 * The "Airlines" row of the trip options popover: any airline, only these, or not these, with a
 * searchable airline picker for the last two. A change re-prices the comparison.
 * @returns The editor.
 * @throws Never.
 */
export const AirlinesEditor = () => {
  // Hooks
  const { workspace, edit } = useEstimator();
  const search = useAirlineSearch();

  // Derived
  const { mode, codes, names } = workspace.airlines;
  const picked: MultiSelectOption[] = codes.map((code) => ({ value: code, label: names[code] ?? code }));
  const found: MultiSelectOption[] = search.results
    .map((airline) => ({ value: airline.iata_code.toUpperCase(), label: airline.name }))
    .filter((option) => !codes.includes(option.value));
  const options = [...picked, ...found];

  // Handlers
  const setMode = (next: AirlineMode) =>
    edit({ type: "setAirlines", airlines: { ...workspace.airlines, mode: next } }, "user");

  const setCodes = (next: string[]) => {
    const labels = new Map(options.map((option) => [option.value, option.label]));
    const kept = next.slice(0, MAX_AIRLINE_PICKS);
    edit(
      {
        type: "setAirlines",
        airlines: {
          mode,
          codes: kept,
          names: Object.fromEntries(kept.map((code) => [code, labels.get(code) ?? code])),
        },
      },
      "user"
    );
  };

  // Render
  return (
    <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">Airlines</h3>
        <div role="group" aria-label="Airlines" className="inline-flex rounded-full border border-gray-200 p-0.5">
          {AIRLINE_MODE_OPTIONS.map((option) => (
            // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
            <button
              key={option.value}
              type="button"
              aria-pressed={mode === option.value}
              onClick={() => setMode(option.value)}
              className="rounded-full px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 aria-pressed:bg-gray-100 aria-pressed:text-gray-900"
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {mode === "any" ? (
        <p className="text-xs font-medium text-gray-600">Spirit and Frontier are left out.</p>
      ) : (
        <FetchMultiselect
          ariaLabel={mode === "only" ? "Airlines to fly" : "Airlines to leave out"}
          options={options}
          selectedValues={codes}
          onChange={setCodes}
          placeholder={mode === "only" ? "Find airlines to fly..." : "Find airlines to leave out..."}
          searchable
          onSearchChange={search.setQuery}
          loading={search.loading}
        />
      )}
    </div>
  );
};
